import type { ReactNode } from 'react';
import { CpmButton } from '../../../components/design-system';
import type { PlaceLoadState } from '../../../types/reportsAdmin.types';
import { ReportsSkeletonList, ReportsWait } from './ReportsDialog';

interface PlaceStateFramesProps {
    state: PlaceLoadState;
    trueEmpty: boolean;
    loadingLabel: string;
    emptyTitle: string;
    emptyBody: string;
    emptyAction?: ReactNode;
    emptyNote?: ReactNode;
    errorTitle: string;
    errorBody: string;
    retryLabel: string;
    onRetry: () => void;
    skeletonRows?: number;
    children: ReactNode;
}

function FrameIcon({ tone, children }: { tone: 'open' | 'danger'; children: ReactNode }) {
    return (
        <div
            className="inline-flex items-center justify-center"
            style={{
                width: 72,
                height: 72,
                borderRadius: 18,
                marginBottom: 20,
                background: tone === 'open' ? 'var(--cpm-open-bg)' : 'var(--cpm-shortfall-bg)',
                color: tone === 'open' ? 'var(--cpm-open-icon)' : 'var(--cpm-shortfall-icon)',
            }}
        >
            {children}
        </div>
    );
}

export default function PlaceStateFrames({
    state,
    trueEmpty,
    loadingLabel,
    emptyTitle,
    emptyBody,
    emptyAction,
    emptyNote,
    errorTitle,
    errorBody,
    retryLabel,
    onRetry,
    skeletonRows = 4,
    children,
}: PlaceStateFramesProps) {
    if (state === 'loading' || state === 'idle') {
        return (
            <div style={{ padding: 20 }}>
                <div style={{ paddingBottom: 18 }}>
                    <ReportsWait label={loadingLabel} gap={10} />
                </div>
                <ReportsSkeletonList rows={skeletonRows} />
            </div>
        );
    }
    if (state === 'error') {
        return (
            <div
                className="flex flex-col items-center text-center"
                style={{ padding: '60px 24px 34px' }}
            >
                <FrameIcon tone="danger">
                    <svg
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                        <path d="M12 9v4M12 17h.01" strokeLinecap="round" />
                    </svg>
                </FrameIcon>
                <div
                    style={{
                        fontSize: 22,
                        fontWeight: 800,
                        letterSpacing: '-0.02em',
                        marginBottom: 8,
                    }}
                >
                    {errorTitle}
                </div>
                <div
                    style={{
                        maxWidth: 470,
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: 1.6,
                        color: 'var(--cpm-text-secondary)',
                    }}
                >
                    {errorBody}
                </div>
                <div style={{ marginTop: 22 }}>
                    <CpmButton variant="navy" size="lg" style={{ height: 42 }} onClick={onRetry}>
                        {retryLabel}
                    </CpmButton>
                </div>
            </div>
        );
    }
    if (trueEmpty) {
        return (
            <div
                className="flex flex-col items-center text-center"
                style={{ padding: '60px 24px 34px' }}
            >
                <FrameIcon tone="open">
                    <svg
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <path d="M4 6h16M4 12h16M4 18h10" strokeLinecap="round" />
                    </svg>
                </FrameIcon>
                <div
                    style={{
                        fontSize: 22,
                        fontWeight: 800,
                        letterSpacing: '-0.02em',
                        marginBottom: 8,
                    }}
                >
                    {emptyTitle}
                </div>
                <div
                    style={{
                        maxWidth: 470,
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: 1.6,
                        color: 'var(--cpm-text-secondary)',
                    }}
                >
                    {emptyBody}
                </div>
                {emptyAction ? <div style={{ marginTop: 22 }}>{emptyAction}</div> : null}
                {emptyNote ? (
                    <div
                        style={{
                            marginTop: 26,
                            paddingTop: 20,
                            borderTop: '1px solid var(--cpm-border-default)',
                            width: '100%',
                            maxWidth: 470,
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--cpm-text-secondary)',
                        }}
                    >
                        {emptyNote}
                    </div>
                ) : null}
            </div>
        );
    }
    return <>{children}</>;
}
